import { AndroidDependenciesReleaseReport, AndroidDependenciesReleaseResponse } from '@/types/androidDependencies';
import {
    FETCH_ANDROID_DEPENDENCIES_FOR_RELEASE_SUCCESS,
    AndroidDependenciesAction,
    UPDATE_ANDROID_DEPENDENCIES_FOR_RELEASE,
    UPDATE_ANDROID_DEPENDENCIES_REPORT_FOR_RELEASE,
} from '@/actions/androidDependencies';
import type { RootState } from '@/reducers/reducers';

export interface AndroidDependenciesState {
    byReleaseId: Record<string, AndroidDependenciesReleaseResponse>;
}

const initialState: AndroidDependenciesState = {
    byReleaseId: {},
};

const mergeReportIntoResponse = (
    existing: AndroidDependenciesReleaseResponse | undefined,
    incoming: AndroidDependenciesReleaseReport,
): AndroidDependenciesReleaseResponse => {
    const baseReports = existing?.reports ?? [];
    const reportIdx = baseReports.findIndex((report) => report.destinationId === incoming.destinationId);
    let mergedReports: AndroidDependenciesReleaseReport[];
    if (reportIdx === -1) {
        mergedReports = [...baseReports, incoming];
    } else {
        mergedReports = [...baseReports];
        mergedReports[reportIdx] = incoming;
    }
    return {
        hasConfigs: mergedReports.length > 0 ? true : (existing?.hasConfigs ?? false),
        reports: mergedReports,
    };
};

export const selectAndroidDependenciesForRelease = (
    state: RootState,
    releaseId: string,
): AndroidDependenciesReleaseResponse | undefined => state.androidDependencies.byReleaseId[releaseId];

export default function androidDependenciesReducer(
    state: AndroidDependenciesState = initialState,
    action: AndroidDependenciesAction,
): AndroidDependenciesState {
    switch (action.type) {
        case FETCH_ANDROID_DEPENDENCIES_FOR_RELEASE_SUCCESS:
        case UPDATE_ANDROID_DEPENDENCIES_FOR_RELEASE: {
            const { releaseId, response } = action.payload;
            return {
                ...state,
                byReleaseId: { ...state.byReleaseId, [releaseId]: response },
            };
        }
        case UPDATE_ANDROID_DEPENDENCIES_REPORT_FOR_RELEASE: {
            const { releaseId, report } = action.payload;
            const merged = mergeReportIntoResponse(state.byReleaseId[releaseId], report);
            return {
                ...state,
                byReleaseId: { ...state.byReleaseId, [releaseId]: merged },
            };
        }
        default:
            return state;
    }
}
